import { useEffect, useRef, useState } from 'react';

interface ScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
  /** Hard ceiling (ms): if the IntersectionObserver hasn't reported the
   *  element visible by now, reveal it anyway. Prevents content from
   *  getting stuck at opacity:0 forever when the observer never fires —
   *  e.g. an element rendered below the fold around async data loads, or
   *  observed after a layout shift. Set to 0 to disable. */
  revealAfterMs?: number;
}

export const useScrollAnimation = (options: ScrollAnimationOptions = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true,
    revealAfterMs = 1200,
  } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // No IntersectionObserver (very old browsers / some embedded webviews):
    // don't hide content we can never reveal.
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const element = ref.current;
    if (!element) {
      // Nothing to observe — reveal so the caller never renders
      // permanently-hidden content.
      setIsVisible(true);
      return;
    }

    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      setIsVisible(true);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          reveal();
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce && revealed) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    // Safety net: reveal even if the observer never reports intersection.
    const timer =
      revealAfterMs > 0
        ? window.setTimeout(() => {
            reveal();
            if (triggerOnce) observer.unobserve(element);
          }, revealAfterMs)
        : undefined;

    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce, revealAfterMs]);

  return { ref, isVisible };
};
